// src/admin/AdminNewsletter.jsx
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';

const API = 'https://madadline.com/api';

/* ============================================================
   STYLES INLINE — pas de fichier CSS séparé
   ============================================================ */
const styles = {
  actionBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    minWidth: 34,
    height: 32,
    padding: '0 10px',
    border: '1px solid #EAE6DA',
    borderRadius: 6,
    background: '#fff',
    color: '#555',
    fontSize: 11,
    fontWeight: 600,
    fontFamily: 'inherit',
    cursor: 'pointer',
    transition: 'all 0.18s ease',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  },
  copy: { color: '#555' },
  copyHover: { background: '#F8F6F0', borderColor: '#C21E56', color: '#C21E56' },
  copyDone: { background: '#16A34A', borderColor: '#16A34A', color: '#fff' },

  whatsapp: {
    background: '#25D366',
    borderColor: '#25D366',
    color: '#fff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 34,
    height: 32,
    padding: '0 10px',
    border: '1px solid #25D366',
    borderRadius: 6,
    fontSize: 13,
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'all 0.18s ease',
  },

  deleteNormal: { color: '#999' },
  deleteHover: { background: '#fef2f2', borderColor: '#fca5a5', color: '#DC2626' },
  deleteConfirm: {
    background: '#DC2626',
    borderColor: '#DC2626',
    color: '#fff',
    animation: 'pulseConfirm 0.8s ease infinite',
  },

  badgeNew: {
    display: 'inline-block',
    marginLeft: 8,
    padding: '2px 8px',
    background: '#C21E56',
    color: '#fff',
    borderRadius: 999,
    fontSize: 9,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    verticalAlign: 'middle',
  },

  statCard: (color, bg) => ({
    padding: 16,
    background: bg,
    border: '1px solid #EAE6DA',
    borderRadius: 10,
    textAlign: 'center',
  }),
  statValue: (color) => ({
    fontSize: 24,
    fontWeight: 700,
    color,
  }),
  statLabel: {
    fontSize: 11,
    color: '#8a7a6e',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: 4,
  },
};

function AdminNewsletter() {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [copiedPhone, setCopiedPhone] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc');
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [hoveredBtn, setHoveredBtn] = useState(null);

  const token = localStorage.getItem('auth_token');

  /* =========================================================
     CHARGEMENT
  ========================================================= */
  const loadSubscribers = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/admin/newsletter`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });
      setSubscribers(Array.isArray(data) ? data : []);
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        (err.response?.status === 401 && 'Session expirée. Reconnectez-vous.') ||
        (err.response?.status === 403 && "Accès refusé — vous n'êtes pas administrateur.") ||
        'Erreur lors du chargement';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubscribers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =========================================================
     STATISTIQUES
  ========================================================= */
  const stats = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now.getFullYear(), now.getMonth(), 1);

    let todayCount = 0, weekCount = 0, monthCount = 0;

    subscribers.forEach(s => {
      if (!s.created_at) return;
      const d = new Date(s.created_at);
      if (d >= today) todayCount++;
      if (d >= weekAgo) weekCount++;
      if (d >= monthAgo) monthCount++;
    });

    return { total: subscribers.length, today: todayCount, week: weekCount, month: monthCount };
  }, [subscribers]);

  /* =========================================================
     FILTRE + TRI
  ========================================================= */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let result = subscribers;

    if (q) {
      result = result.filter(s =>
        (s.phone || '').toLowerCase().includes(q) ||
        (s.email || '').toLowerCase().includes(q)
      );
    }

    result = [...result].sort((a, b) => {
      const da = a.created_at ? new Date(a.created_at).getTime() : 0;
      const db = b.created_at ? new Date(b.created_at).getTime() : 0;
      return sortOrder === 'desc' ? db - da : da - db;
    });

    return result;
  }, [subscribers, search, sortOrder]);

  /* =========================================================
     HELPERS
  ========================================================= */
  const handleCopy = async (phone) => {
    try {
      await navigator.clipboard.writeText(phone);
      setCopiedPhone(phone);
      setTimeout(() => setCopiedPhone(null), 1500);
    } catch {}
  };

  const isNew = (dateStr) => {
    if (!dateStr) return false;
    const diff = (Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60);
    return diff <= 48;
  };

  const whatsappLink = (phone) => {
    const cleaned = (phone || '').replace(/\D/g, '');
    return `https://wa.me/${cleaned}`;
  };

  /* =========================================================
     SUPPRESSION
  ========================================================= */
  const handleDelete = async (id) => {
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id);
      setTimeout(() => setConfirmDeleteId(current => current === id ? null : current), 3000);
      return;
    }

    setDeletingId(id);
    try {
      await axios.delete(`${API}/admin/newsletter/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubscribers(prev => prev.filter(s => s.id !== id));
      setConfirmDeleteId(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la suppression');
    } finally {
      setDeletingId(null);
    }
  };

  /* =========================================================
     EXPORT CSV
  ========================================================= */
  const exportCSV = () => {
    const rows = [['#', 'Téléphone', 'Date inscription']];
    filtered.forEach((s, i) => {
      rows.push([
        i + 1,
        s.phone,
        s.created_at ? new Date(s.created_at).toLocaleString('fr-FR') : '',
      ]);
    });
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter_madad_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* =========================================================
     RENDER
  ========================================================= */
  return (
    <div className="admin-newsletter">
      {/* ⭐ Styles globaux (animations + hover via CSS) */}
      <style>{`
        @keyframes pulseConfirm {
          0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.5); }
          50%      { box-shadow: 0 0 0 6px rgba(220, 38, 38, 0); }
        }
        .anl-action:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 6px rgba(0,0,0,0.08);
        }
      `}</style>

      <header className="admin-page-head">
        <div>
          <h1>Inscrites Newsletter</h1>
          <p>{subscribers.length} inscrite{subscribers.length > 1 ? 's' : ''} au total</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={loadSubscribers} className="admin-btn" disabled={loading}>
            ↻ Actualiser
          </button>
          <button
            onClick={exportCSV}
            className="admin-btn admin-btn--primary"
            disabled={filtered.length === 0}
          >
            ↓ Exporter CSV
          </button>
        </div>
      </header>

      {/* Statistiques */}
      {!loading && !error && subscribers.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 12,
          marginBottom: 20,
        }}>
          {[
            { label: 'Total',         value: stats.total, color: '#2b2b2b', bg: '#F8F6F0' },
            { label: "Aujourd'hui",   value: stats.today, color: '#16A34A', bg: '#f0fdf4' },
            { label: 'Cette semaine', value: stats.week,  color: '#2563EB', bg: '#eff6ff' },
            { label: 'Ce mois',       value: stats.month, color: '#C21E56', bg: '#fbeef2' },
          ].map(stat => (
            <div key={stat.label} style={styles.statCard(stat.color, stat.bg)}>
              <div style={styles.statValue(stat.color)}>{stat.value}</div>
              <div style={styles.statLabel}>{stat.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filtres */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          type="search"
          placeholder="Rechercher par téléphone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: '1 1 250px',
            padding: '10px 14px',
            border: '1px solid #EAE6DA',
            borderRadius: 8,
            fontSize: 14,
            fontFamily: 'inherit',
          }}
        />
        <select
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          style={{
            padding: '10px 14px',
            border: '1px solid #EAE6DA',
            borderRadius: 8,
            fontSize: 14,
            fontFamily: 'inherit',
            background: '#fff',
            cursor: 'pointer',
          }}
        >
          <option value="desc">Plus récentes d'abord</option>
          <option value="asc">Plus anciennes d'abord</option>
        </select>
      </div>

      {loading && (
        <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Chargement…</div>
      )}

      {!loading && error && <div className="admin-alert admin-alert--error">{error}</div>}

      {!loading && !error && filtered.length === 0 && (
        <div style={{
          padding: 40, textAlign: 'center', color: '#888',
          background: '#F8F6F0', borderRadius: 12,
        }}>
          <p>{search ? 'Aucun résultat pour cette recherche.' : 'Aucune inscrite pour le moment.'}</p>
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div style={{
          background: '#fff',
          border: '1px solid #EAE6DA',
          borderRadius: 12,
          overflow: 'hidden',
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
              <thead>
                <tr style={{ background: '#F8F6F0', textAlign: 'left' }}>
                  <th style={{ padding: 14, fontSize: 12, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5, width: 50 }}>#</th>
                  <th style={{ padding: 14, fontSize: 12, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5 }}>Téléphone</th>
                  <th style={{ padding: 14, fontSize: 12, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5 }}>Date d'inscription</th>
                  <th style={{ padding: 14, fontSize: 12, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5, width: 220 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s, i) => {
                  const copyKey = `copy-${s.id}`;
                  const deleteKey = `delete-${s.id}`;
                  const isCopied = copiedPhone === s.phone;
                  const isConfirming = confirmDeleteId === s.id;
                  const isDeleting = deletingId === s.id;

                  return (
                    <tr key={s.id} style={{ borderTop: '1px solid #f0e8e0' }}>
                      <td style={{ padding: 14, fontSize: 13, color: '#888' }}>{i + 1}</td>
                      <td style={{ padding: 14, fontSize: 14, fontWeight: 600, color: '#2b2b2b' }}>
                        {s.phone}
                        {isNew(s.created_at) && (
                          <span style={styles.badgeNew}>Nouveau</span>
                        )}
                      </td>
                      <td style={{ padding: 14, fontSize: 13, color: '#555' }}>
                        {s.created_at
                          ? new Date(s.created_at).toLocaleString('fr-FR', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '—'}
                      </td>
                      <td style={{ padding: 14 }}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {/* Copier */}
                          <button
                            onClick={() => handleCopy(s.phone)}
                            onMouseEnter={() => setHoveredBtn(copyKey)}
                            onMouseLeave={() => setHoveredBtn(null)}
                            className="anl-action"
                            style={{
                              ...styles.actionBtn,
                              ...(isCopied ? styles.copyDone : {}),
                              ...(hoveredBtn === copyKey && !isCopied ? styles.copyHover : {}),
                            }}
                          >
                            {isCopied ? '✓ Copié' : '📋 Copier'}
                          </button>

                          {/* WhatsApp */}
                          <a
                            href={whatsappLink(s.phone)}
                            target="_blank"
                            rel="noreferrer"
                            title="Ouvrir WhatsApp"
                            className="anl-action"
                            onMouseEnter={() => setHoveredBtn(`wa-${s.id}`)}
                            onMouseLeave={() => setHoveredBtn(null)}
                            style={styles.whatsapp}
                          >
                            💬
                          </a>

                          {/* Supprimer */}
                          <button
                            onClick={() => handleDelete(s.id)}
                            disabled={isDeleting}
                            onMouseEnter={() => setHoveredBtn(deleteKey)}
                            onMouseLeave={() => setHoveredBtn(null)}
                            className="anl-action"
                            style={{
                              ...styles.actionBtn,
                              ...(isConfirming ? styles.deleteConfirm : styles.deleteNormal),
                              ...(hoveredBtn === deleteKey && !isConfirming ? styles.deleteHover : {}),
                              cursor: isDeleting ? 'wait' : 'pointer',
                            }}
                          >
                            {isDeleting ? '…' : isConfirming ? '⚠ Confirmer' : '🗑'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminNewsletter;